import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useParallaxRef } from '@/utils/parallax';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useGradientBackground } from '@/utils/gradientBackground';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  name: string;
  questions: FAQItem[];
}

const faqCategories: FAQCategory[] = [
  {
    name: 'Money & Commitment',
    questions: [
      {
        question: 'How does pricing work for services?',
        answer:
          "Each package has a clear starting price — Starter from $2,000, Basic from $5,000, and Pro from $10,000 with one automation, $13,000 with two, or $15,000 with three. After our initial consult, I'll send a written proposal that confirms the exact scope, timeline, and investment before you commit. No surprise line items, no \"scope creep\" tricks.",
      },
      {
        question: 'Do I need to pay everything upfront?',
        answer:
          "No. Typically there's an initial deposit to reserve your spot, then one or two milestone payments tied to clear project phases (for example: kickoff, design approval, launch). You'll always know what's due when, and everything runs through a secure online invoice/checkout so you don't have to manage payments manually.",
      },
      {
        question: 'What happens if the scope changes mid-project?',
        answer:
          "It's normal for new ideas to come up. When that happens, we pause, separate the \"must-haves\" from the \"nice-to-haves,\" and update the scope together. Small tweaks are usually absorbed. Bigger changes become a clearly priced add-on or a Phase 2, so the original project can finish on time and you're not stuck in an endless redo.",
      },
    ],
  },
  {
    name: 'Process & Timeline',
    questions: [
      {
        question: 'How long does a typical project take?',
        answer:
          "It depends on the package and how ready your content is, but most projects fall into these ranges:\n\n• Starter Branding: about 1–2 weeks\n• Basic Brand + Website: about 3–5 weeks\n• Pro Launch Studio: about 5–8 weeks\n\nYou'll get a project timeline in your proposal, and we'll agree on check-in points so you always know what's happening next.",
      },
      {
        question: 'What do you need from me to get started?',
        answer:
          "At minimum: your domain, a short intake form, and a call where we talk through your services, clients, and goals. If you already have assets (past site, logo, copy, brochures), we'll reuse what's still working. If you don't, that's fine — I'll guide you through a simple intake so we can create what's needed without you writing a novel.",
      },
      {
        question: 'How involved do I need to be during the project?',
        answer:
          "You don't need to live in your inbox. Most clients prefer a focused start, a few key review points, and clear deadlines for feedback. Between those checkpoints, I move things forward. You'll always know when your input is needed and what decisions we're making, but you won't have to manage the build day-to-day.",
      },
    ],
  },
  {
    name: 'Tech & Ownership',
    questions: [
      {
        question: 'Do you handle domain, hosting, and all the tech setup?',
        answer:
          "Yes — especially for the Basic and Pro packages. I'll take care of connecting your domain, setting up reliable hosting, enabling SSL, and wiring up forms or booking tools so enquiries don't go into a black hole. If you already have a setup you like, we can either keep it and clean it up or migrate everything to a simpler, faster stack.",
      },
      {
        question: 'What happens if something breaks after launch?',
        answer:
          "I don't disappear after launch. Pro projects include follow-up support calls and a simple \"what to do if…\" checklist. If something small needs fixing soon after launch, I'll handle it. If you'd like an ongoing support or improvement plan, we can add that as a lightweight retainer once the main project wraps.",
      },
      {
        question: "Who owns the website and brand assets when we're done?",
        answer:
          "You do. Once the project is complete and paid for, you own your domain, your website, and your brand assets. You'll receive access to your site, design files or exports, and a simple summary of where everything lives so you're not locked into a mystery system.",
      },
    ],
  },
  {
    name: 'Fit, Scope & Domains',
    questions: [
      {
        question: 'Do I have to buy my domain from AttainDomains.com to work with you?',
        answer:
          "No. I love when clients use strong domains from AttainDomains.com, but it's not required. If you already have a domain you like, we can build your brand and site around it. If you're still deciding on a domain, I can also help you evaluate options and explain what will age well as your business grows.",
      },
      {
        question: 'What types of businesses are a good fit?',
        answer:
          'The best fits are people who sell expertise: consultants, coaches, small firms, micro-agencies, and specialist practices (like legal, accounting, HR, recruiting, training, or creative services). If your business depends on trust and conversations more than raw traffic, this process is built for you.',
      },
      {
        question: 'What if I already have a brand or website?',
        answer:
          "That's totally fine. We'll start with what's working and what isn't. Sometimes the right move is a focused refresh and clearer messaging; other times it's cleaner to rebuild using your existing assets. The first consult is where we decide whether we're in \"refine\" mode or \"rebuild\" mode.",
      },
      {
        question: "What if I'm not happy with the result?",
        answer:
          "The process is built around avoiding that outcome. You'll see work in stages, give feedback at clear checkpoints, and know what's coming next. If something feels off, we address it while it's still easy to adjust. The goal isn't just to ship a site — it's to ship something you actually use and feel confident sending to clients.",
      },
    ],
  },
];

export default function ServicesFAQSection() {
  // Parallax for subtitle
  const subtitleRef = useParallaxRef({
    speed: -0.12,
    axis: 'y',
    maxTranslate: 10,
  });

  // Parallax for H2 (minimal or none)
  const titleRef = useParallaxRef({
    speed: -0.06,
    axis: 'y',
    maxTranslate: 6,
  });

  // Reveal hook for section entrance
  const sectionReveal = useRevealOnce({ amount: 0.2 });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut' as const,
      },
    },
  };

  // Generate unique IDs for accordion items
  let questionIndex = 0;

  const backgroundStyle = useGradientBackground();

  return (
    <section className="relative w-full text-slate-900 py-16 md:py-24" style={backgroundStyle}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            ref={sectionReveal.ref}
            variants={containerVariants}
            initial="hidden"
            animate={sectionReveal.controls}
          >
            {/* Heading Section */}
            <div className="text-center mb-12 md:mb-16">
              <h2
                ref={titleRef as any}
                className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6"
              >
                Frequently asked questions about working together
              </h2>
              <p
                ref={subtitleRef as any}
                className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto"
              >
                These are the most common questions people ask before moving forward. If you don't see yours here, we can talk it through on a free 1-hour consult.
              </p>
            </div>

            {/* FAQ Accordion */}
            <div className="mb-12 md:mb-16">
              <Accordion type="multiple" className="w-full">
                {faqCategories.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="mb-8">
                    {/* Category Heading */}
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 mt-8 first:mt-0">
                      {category.name}
                    </h3>

                    {/* Category Questions */}
                    {category.questions.map((faq) => {
                      const itemId = `faq-${categoryIndex}-${questionIndex++}`;
                      return (
                        <AccordionItem key={itemId} value={itemId} className="border-b border-slate-200">
                          <AccordionTrigger className="text-left py-4 text-base font-medium text-slate-900 hover:no-underline">
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent className="text-slate-600 leading-relaxed pt-2 pb-4">
                            <div className="whitespace-pre-line">{faq.answer}</div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </div>
                ))}
              </Accordion>
            </div>

            {/* Bottom Link */}
            <div className="text-center">
              <span className="text-slate-700">Still have a question? </span>
              <Link
                to="/services/consult"
                className="text-blue-600 font-medium hover:underline transition-colors duration-200"
              >
                Book a free 1-hour consult →
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

